import { getJourneyEnglishQuality } from '@/data/journeyEnglishQuality';
import { getJourneyExplanationTranslation } from '@/data/journeyExplanationTranslations';
import { JOURNEY_LIBRARY_BY_ID } from '@/data/journeyLibrary';
import { getGenericJourneyStageTitleTranslation } from '@/data/genericJourneyStageTitleTranslations';
import { splitJourneyExplanationText } from '@/lib/journeyExplanationPresentation';

const GENERIC_SOURCE_TRANSLATION_IDS = Object.freeze({
  'generic-day-surgery': 'kch-day-surgery',
  'generic-maternity': 'kch-maternity',
  'generic-outpatients': 'kch-outpatient-department',
  'generic-emergency-department': 'kch-emergency-department',
  'generic-imaging-diagnostics': 'kch-imaging-diagnostics',
  'generic-inpatient-ward': 'kch-inpatient-ward',
  'generic-critical-care-visitor': 'kch-critical-care-visitor',
  'generic-surgery-operation': 'kch-planned-inpatient-surgery',
  'generic-inpatient-admission': 'kch-planned-inpatient-medical',
  'generic-cancer-care': 'kch-cancer-care',
  'generic-paediatrics': 'kch-childrens-services',
});

// Enrichment deliberately keeps concise semantic IDs. Some established locale
// resources pre-date that model and use the longer patient-facing stage IDs.
// Keep the mapping here so a richer English stage can never make translated
// content unreachable simply because its internal ID was normalised.
const LOCALIZED_STAGE_ID_ALIASES = Object.freeze({
  'generic-maternity': Object.freeze({
    pregnancy: 'during-pregnancy',
    'preparing-birth': 'preparing-for-birth',
    'labour-birth': 'labour-and-birth',
    'after-birth': 'after-birth',
  }),
});

function candidateStageIds(journeyId, stageId) {
  const alias = LOCALIZED_STAGE_ID_ALIASES[journeyId]?.[stageId];
  return [...new Set([stageId, alias].filter(Boolean))];
}

function translatedStageField(i18n, language, journeyId, stageId, field) {
  const ids = [journeyId, GENERIC_SOURCE_TRANSLATION_IDS[journeyId]].filter(Boolean);
  for (const id of ids) {
    for (const candidateStageId of candidateStageIds(journeyId, stageId)) {
      const value = i18n?.getResource?.(language, 'hospitalJourney', `journeys.${id}.stages.${candidateStageId}.${field}`);
      if (typeof value === 'string' && value.trim()) return value;
    }
  }
  return null;
}

function templateIdForJourney(journeyId) {
  return String(journeyId || '').replace(/^generic-/, '');
}

function canonicalStageTitle(journeyId, stageId) {
  const template = JOURNEY_LIBRARY_BY_ID[templateIdForJourney(journeyId)];
  return template?.stages?.find((stage) => stage.id === stageId)?.title || null;
}

function canonicalStageDescription(journeyId, stageId) {
  return getJourneyEnglishQuality(templateIdForJourney(journeyId))?.stages?.[stageId] || null;
}

function translatedExplanationSummary(explanation) {
  return splitJourneyExplanationText(explanation?.whatHappens || '')[0] || null;
}

function canUseGenericFallback(currentValue, canonicalValue) {
  return !canonicalValue || String(currentValue || '').trim() === String(canonicalValue || '').trim();
}

export function localizeGenericJourneyStages(i18n, language, journeyId, stages = []) {
  const normalizedLanguage = String(language || 'en').toLowerCase().split('-')[0];
  if (normalizedLanguage === 'en') return stages;

  return (stages || []).map((stage) => {
    const localizedExplanation = getJourneyExplanationTranslation(normalizedLanguage, journeyId, stage.id);
    const { explanation: _englishExplanation, roleIds: _englishRoleIds, ...safeStage } = stage;

    const resourceTitle = translatedStageField(i18n, normalizedLanguage, journeyId, stage.id, 'title');
    const fallbackTitle = getGenericJourneyStageTitleTranslation(normalizedLanguage, journeyId, stage.id);
    const title = resourceTitle || (
      fallbackTitle && canUseGenericFallback(safeStage.title, canonicalStageTitle(journeyId, stage.id))
        ? fallbackTitle
        : null
    );

    const resourceDescription = translatedStageField(i18n, normalizedLanguage, journeyId, stage.id, 'description');
    const fallbackDescription = translatedExplanationSummary(localizedExplanation);
    const description = resourceDescription || (
      fallbackDescription && canUseGenericFallback(safeStage.description, canonicalStageDescription(journeyId, stage.id))
        ? fallbackDescription
        : null
    );

    return {
      ...safeStage,
      title: title || safeStage.title,
      description: description || safeStage.description,
      // Role IDs contain no English prose. Preserve requested variant identity
      // even when a translated explanatory section is not yet available.
      ...(stage.roleIds?.length ? { roleIds: stage.roleIds } : {}),
      ...(localizedExplanation ? { explanation: localizedExplanation } : {}),
    };
  });
}

export { GENERIC_SOURCE_TRANSLATION_IDS, LOCALIZED_STAGE_ID_ALIASES };
